"use client";

import { useInfiniteQuery } from "@tanstack/react-query";
import { useRef } from "react";
import { type FeedEntry, type Page } from "@theta/api-client";
import { thetaApi, pollablePageOf } from "@/lib/api/theta";
import { retryUnlessRefused } from "./queryRetry";

type FeedSort = "newest" | "recent_comments" | "popular";

/**
 * THE HOME FEED, ON /api/v1 — `GET /feed`
 *
 * What went away with `lib/api/feed.ts`:
 *
 *  - TWO PAGINATIONS. The old endpoint was cursor-paged for `newest` and
 *    OFFSET-paged for the other two sorts, so `getNextPageParam` had to read
 *    `next_before` or `page + 1` depending on the sort — and a sort switch
 *    that forgot to reset the page param silently served page one forever.
 *    v1 is cursor-only (§1.3/D4): one `nextCursor`, every sort.
 *  - THE 10 SECOND BLIND REFETCH of the whole first page. It still polls, but
 *    `?ifChanged=` answers `{unchanged: true}` with no document while nothing
 *    the feed renders has changed (§1.10), and the SERVER sets the cadence
 *    through `meta.pollAfterMs` (D12) rather than a constant compiled into
 *    this file.
 *
 * The conditional-read cache is the same shape `useV1GroupPosts` uses and for
 * the same reason: `?ifChanged=` resolves to NO document, but a `queryFn` must
 * return the page, so an unchanged tick returns the SAME OBJECT REFERENCE and
 * react-query does not re-render. It lives in a ref rather than module scope so
 * two feeds cannot share one, and it is keyed by sort as well as cursor because
 * a token is only valid for the document it came from.
 */
const FALLBACK_POLL_MS = 10_000;

export function useHomeFeed(sort: FeedSort = "newest") {
  const seen = useRef(new Map<string, { state: string; page: Page<FeedEntry> }>());
  const pollAfterMs = useRef<number | null>(null);

  return useInfiniteQuery({
    queryKey: ["v1", "home-feed", sort],
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }) => {
      const key = `${sort}|${pageParam ?? ""}`;
      const previous = seen.current.get(key);

      const result = pollablePageOf<FeedEntry>(
        await thetaApi.http.GET("/feed", {
          params: {
            query: {
              sort,
              ...(pageParam ? { cursor: pageParam } : {}),
              ...(previous ? { ifChanged: previous.state } : {}),
            },
          },
        })
      );

      pollAfterMs.current = result.pollAfterMs;

      if (result.unchanged) {
        return (
          previous?.page ?? { items: [], nextCursor: null, prevCursor: null, hasMore: false }
        );
      }

      seen.current.set(key, { state: result.state ?? "", page: result.data });
      return result.data;
    },
    getNextPageParam: (last) => (last.hasMore ? last.nextCursor : undefined),
    // A 404 is a real answer here as everywhere (D7) — retrying it is retrying
    // a correct response.
    retry: retryUnlessRefused,

    // Bounded to the FIRST page, exactly as the group feed is: an infinite
    // query refetches every loaded page on each tick, and somebody who has
    // scrolled into history is reading, not watching for new posts.
    refetchInterval: (query) =>
      typeof document !== "undefined" &&
      document.visibilityState !== "hidden" &&
      (query.state.data?.pages.length ?? 0) <= 1
        ? (pollAfterMs.current ?? FALLBACK_POLL_MS)
        : (false as const),
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    staleTime: 0,
    gcTime: 5 * 60 * 1000,
  });
}

/** The query key a write invalidates. */
export const homeFeedKey = ["v1", "home-feed"];
